/**
 * La place de chaque chose sur la scène du live : une règle pure, mesurée sur la taille de la scène.
 *
 * Trois choses s'y décident.
 *  - **Le tandem dans la visio** (Wajih, 19/10) : quand la visio est à l'écran, Sam et le masteur s'y
 *    tiennent, sans bulle en plus. En Concentré (et pendant l'attente), Sam est devant, en grand ; le
 *    masteur passe derrière lui en **visage rond, comme dans la bulle tandem** (Wajih, 20/10 ; Nada,
 *    20/10 : sa vidéo restait un grand rectangle, Sam à cheval sur son coin), sous un voile sombre léger.
 *    **Qui parle, et d'où : une perspective** (Wajih, 22/10 : la bulle de Sam, posée au-dessus des deux,
 *    chevauchait la visio du masteur et s'attribuait à lui ou à un autre élève) : celui qui est devant se
 *    tient en bas à gauche, celui qui est en retrait en haut à droite, et **le duo Sam + masteur se montre
 *    légèrement superposé, comme la miniature, à toutes les tailles** — la même taille, le même décalage,
 *    le même recouvrement que la bulle tandem (`tandemBubbleGeometry`), mis à l'échelle. Ce que dit Sam sort
 *    juste au-dessus de lui, aligné sur son bord gauche (`peek`) : elle peut longer le rond du masteur,
 *    jamais couvrir son visage ; l'étiquette du Concentré se pose au coin bas droit du rond, comme la
 *    marque de la miniature, hors du visage elle aussi.
 *    En Dialogue, la vidéo reprend toute la scène et Sam passe en petit, opaque, **à côté du masteur**
 *    (Wajih, 20/10 : posé au-dessus de ma vignette, il était « mal placé ») : juste au-dessus de son
 *    prénom, en bas à gauche ; quand il partage son écran, à côté de sa caméra.
 *  - **Les coins**, pour que rien ne se chevauche quel que soit l'état des micros et des caméras : sur la
 *    vidéo du masteur, la main levée en haut à gauche, le micro coupé en haut à droite, le nom en bas à
 *    gauche (`<VideoTile>`) ; en Concentré, l'étiquette du mode en haut à gauche ; **ma vignette toujours en
 *    bas à droite**, le seul coin libre — en haut à droite, elle cachait le micro coupé du masteur.
 *  - **Le partage d'écran** (Wajih, 20/10 : la caméra du masteur, posée dans le coin de l'image, la
 *    chevauchait trop) : l'image partagée se retire de la rangée des visages — sa caméra, Sam et ma
 *    vignette — qui se pose là où l'image perd le moins : sous elle (un écran debout, un volet large),
 *    ou à sa droite (le partage en grand). Rien n'y mord sur l'image ; le bouton qui l'agrandit se
 *    range avec eux.
 *  - **Les petits ronds** (Wajih, 20/10) : les autres tuteurs connectés et une autre caméra du masteur,
 *    sans tandem ni place en grand, en colonne au bord droit, ou dans la rangée du partage.
 *  - **Mes autres appareils** (Wajih, daily du 21/10) : connecté ailleurs aussi (un navigateur resté
 *    ouvert), je le vois sur une bande collée en haut de ma vignette (`twins`) ; ma vignette reste
 *    l'appareil où je suis. La bande compte dans ce que ma vignette occupe : rien ne se pose dessus.
 */

import { TWIN_CELLS, TWIN_LABEL } from "../../molecules/LiveTwinStrip/liveTwinStripGeometry"
import { SAM_PEEK_MAX_HEIGHT } from "../../molecules/SamPeek/samPeekGeometry"
import { MARK_INSET, STAGE_MARK, STAGE_NAME_MAX_RATIO, VIDEO_THUMB } from "../../molecules/VideoTile/videoTileGeometry"
import {
    TANDEM_BACK_SHIFT_Y,
    TANDEM_FACE,
    TANDEM_TUTOR_BACK_SCALE,
    TANDEM_TUTOR_BACK_SHIFT_X,
} from "../LiveTandemBubble/tandemBubbleGeometry"

/** La marge d'un coin de la scène (`spacing.3`). */
export const STAGE_INSET = 12
/** Ma vignette (`<VideoTile size="thumb">`), et la caméra du masteur quand il partage son écran. */
export const THUMB = VIDEO_THUMB
export { MARK_INSET, STAGE_MARK }
/** La hauteur du nom sur la vidéo de la scène (`text-md`, sa marge comprise). */
export const STAGE_NAME_HEIGHT = 32
/** Sam en petit, en Dialogue : la taille d'un visage de la bulle tandem. */
export const SAM_SMALL = 56
/** L'écart entre Sam en petit et ce à côté de quoi il se tient. */
const SAM_SMALL_GAP = 8
/** Le bouton qui agrandit le partage (`<IconButton>`, comme « Revenir à l'activité »). */
export const STAGE_BUTTON = 36
/** Un écran partagé : un écran d'ordinateur, en 16:9. */
export const SHARE_ASPECT = 16 / 9

/**
 * La vidéo du masteur derrière Sam : sous un voile sombre léger (Wajih, 19/10 : l'estompe laissait voir
 * le fond clair de la scène, un gris délavé).
 */
export const TUTOR_BACK_VEIL = 0.45
/**
 * Le visage rond du masteur derrière Sam, **comme dans la miniature** (`<LiveTandemBubble>`, Wajih 22/10 :
 * « la superposition des miniatures, à l'identique en grand ») : sa taille et son décalage, en part de la
 * taille de Sam. Les deux ronds se recouvrent légèrement, à toutes les tailles.
 */
export const TUTOR_ROUND_RATIO = TANDEM_TUTOR_BACK_SCALE
export const TUTOR_ROUND_SHIFT = { x: TANDEM_TUTOR_BACK_SHIFT_X / TANDEM_FACE, y: TANDEM_BACK_SHIFT_Y / TANDEM_FACE }
/** Le visage du masteur dans son rond, en part de son rayon : ce que la bulle de Sam ne couvre jamais. */
export const TUTOR_FACE_RATIO = 0.65
/** L'étiquette du Concentré sur le visage rond (`<SoundModeBadge>`) : la place qu'on lui garde. */
export const TUTOR_MARK = { width: 96, height: 28 }
/** L'écart entre l'étiquette et le visage du masteur. */
const MARK_GAP = 2
/** L'écart entre ce que dit Sam et ce qu'il surmonte. */
const PEEK_GAP = 8

export type Point = { x: number; y: number }
export type Rect = { x: number; y: number; width: number; height: number }

/** Le partage d'écran : l'image, et la rangée des visages qui s'en retire. */
export type ShareLayout = {
    /** La place de l'image partagée (elle s'y pose entière). */
    screen: Rect
    /** La caméra du masteur. */
    tutorCam: Rect
    /** Le bouton qui agrandit le partage, offert au grand écran. */
    fullScreen: Rect
}

export type StageLayout = {
    /** Ma vignette : en bas à droite, dans les deux modes. */
    me: Rect
    /** Sam devant (Concentré) : sa taille et son centre. */
    samFront: { size: number; center: Point }
    /** Sam en petit (Dialogue) : son centre, et l'échelle qui le ramène de sa taille de devant à `SAM_SMALL`. */
    samSmall: { center: Point; scale: number }
    /**
     * La vidéo du masteur derrière Sam, le temps de l'orbite : son centre et son échelle (sa taille pleine :
     * la scène). Elle s'y fond dans le visage rond (`tutorRound`).
     */
    tutorBack: { center: Point; scale: number }
    /** Le visage rond du masteur derrière Sam (Concentré, attente) : son centre et son diamètre. */
    tutorRound: { center: Point; size: number }
    /**
     * Ce que dit Sam, quand il est devant : son coin bas gauche, juste au-dessus de lui, sur son bord gauche,
     * et la largeur où elle tient, hors de la colonne des petits ronds. Elle peut longer le rond du masteur,
     * jamais son visage.
     */
    peek: Point & { width: number }
    /** La place de l'étiquette du Concentré : au coin bas droit du visage rond, hors de son visage. */
    tutorMark: Rect
    /** Le masteur partage son écran (Dialogue seulement). Sinon `null`. */
    share: ShareLayout | null
    /** Les petits ronds (`rounds` demandés), autant que la place en tient. */
    rounds: Rect[]
    /** La bande de mes autres appareils, collée en haut de ma vignette ; `null` sans autre appareil. */
    twins: Rect | null
}

/**
 * La bande de mes autres appareils, collée en haut de ma vignette : sa hauteur, « Aussi connecté » au-dessus
 * des cases compris (Wajih, 21/10).
 */
export const TWIN_STRIP = TWIN_LABEL + TWIN_CELLS
/** Le filet entre la bande et ma vignette : collées, deux pièces. */
export const TWIN_SEAM = 2

/** Un petit rond : un autre tuteur connecté, ou une autre caméra du masteur (`<VideoTile size="round">`). */
export const ROUND = 40
/** L'écart entre deux ronds, et entre un rond et ce qu'il côtoie. */
const ROUND_GAP = 8

/** Autant de ronds que la place en tient, le long d'une ligne, à partir d'un bout. */
function roundsAlong(count: number, start: Point, step: Point, room: number): Rect[] {
    const fit = Math.max(0, Math.floor((room + ROUND_GAP) / (ROUND + ROUND_GAP)))
    return Array.from({ length: Math.min(count, fit) }, (_, index) => ({
        x: start.x + index * step.x,
        y: start.y + index * step.y,
        width: ROUND,
        height: ROUND,
    }))
}

/**
 * Les ronds des autres tuteurs (Wajih, 20/10) : sans partage, une colonne au bord droit, sous le coin
 * haut droit (le micro coupé du masteur, « Revenir à l'activité ») et au-dessus de ma vignette ; au
 * partage, dans la rangée des visages — au-dessus de Sam puis vers la gauche sous l'image, ou entre
 * Sam et le bouton du plein écran à sa droite. Ce qui ne tient pas ne se pose pas.
 */
function roundsLayout(
    count: number,
    width: number,
    me: Rect,
    meTop: number,
    share: ShareLayout | null,
    samSmall: Point
): Rect[] {
    if (count <= 0) return []
    const step = ROUND + ROUND_GAP
    if (share == null) {
        const top = STAGE_INSET + STAGE_BUTTON + ROUND_GAP
        const x = width - STAGE_INSET - ROUND
        return roundsAlong(count, { x, y: top }, { x: 0, y: step }, meTop - ROUND_GAP - top)
    }
    // L'image sur toute la largeur : la rangée est sous elle. Les ronds en haut de la rangée, le premier
    // au-dessus de Sam, les suivants vers la gauche ; le bouton du plein écran, lui, est en bas.
    if (share.screen.width >= width) {
        const x = samSmall.x - ROUND / 2
        return roundsAlong(count, { x, y: me.y }, { x: -step, y: 0 }, x + ROUND - STAGE_INSET)
    }
    const top = samSmall.y + SAM_SMALL / 2 + ROUND_GAP
    const x = me.x + me.width - ROUND
    return roundsAlong(count, { x, y: top }, { x: 0, y: step }, share.fullScreen.y - ROUND_GAP - top)
}

/** La surface d'une image `aspect` posée entière dans `width` × `height`. */
function fittedArea(width: number, height: number): number {
    const w = Math.max(0, Math.min(width, height * SHARE_ASPECT))
    return w * (w / SHARE_ASPECT)
}

/**
 * La rangée des visages au partage : sous l'image ou à sa droite, là où l'image perd le moins.
 * Sous elle : ma vignette au coin bas droit, la caméra du masteur à sa gauche, Sam à la gauche de
 * celle-ci, le bouton du plein écran au bout. À droite : la caméra en haut, Sam dessous, ma vignette en
 * bas, le bouton juste au-dessus d'elle.
 */
function shareLayout(width: number, height: number, me: Rect, meTop: number): { share: ShareLayout; samSmall: Point } {
    const band = me.y + me.height - meTop + 2 * STAGE_INSET
    const column = THUMB.width + 2 * STAGE_INSET
    const below = fittedArea(width, height - band) >= fittedArea(width - column, height)
    if (below) {
        const tutorCam: Rect = { ...THUMB, x: me.x - STAGE_INSET - THUMB.width, y: me.y }
        return {
            share: {
                screen: { x: 0, y: 0, width, height: height - band },
                tutorCam,
                fullScreen: {
                    x: STAGE_INSET,
                    y: height - STAGE_INSET - STAGE_BUTTON,
                    width: STAGE_BUTTON,
                    height: STAGE_BUTTON,
                },
            },
            samSmall: {
                x: tutorCam.x - SAM_SMALL_GAP - SAM_SMALL / 2,
                y: tutorCam.y + tutorCam.height - SAM_SMALL / 2,
            },
        }
    }
    const tutorCam: Rect = { ...THUMB, x: me.x, y: STAGE_INSET }
    return {
        share: {
            screen: { x: 0, y: 0, width: width - column, height },
            tutorCam,
            fullScreen: {
                x: me.x + me.width - STAGE_BUTTON,
                y: meTop - STAGE_INSET - STAGE_BUTTON,
                width: STAGE_BUTTON,
                height: STAGE_BUTTON,
            },
        },
        samSmall: {
            x: tutorCam.x + tutorCam.width - SAM_SMALL / 2,
            y: tutorCam.y + tutorCam.height + SAM_SMALL_GAP + SAM_SMALL / 2,
        },
    }
}

/** Du bas de Sam au haut du visage du masteur, en part de la taille de Sam : ce que le duo prend en hauteur. */
const DUO_REACH = Math.max(1, 0.5 - TUTOR_ROUND_SHIFT.y + (TUTOR_ROUND_RATIO / 2) * TUTOR_FACE_RATIO)

/**
 * Le duo en largeur, Sam devant à `size` au bord gauche de la scène : le rond du masteur, son visage, et
 * l'étiquette du Concentré au coin bas droit du rond, comme la marque de la miniature — son coin haut
 * gauche juste hors du visage, qu'elle ne couvre jamais. Repoussée vers la gauche par ce qui garde le bord
 * droit (`edge`, les petits ronds), elle descend d'autant le long du visage (`markDown`, depuis le centre
 * du rond). `right` : le bord droit de tout le duo.
 */
function duoAcross(size: number, edge: number) {
    const roundSize = Math.round(size * TUTOR_ROUND_RATIO)
    const tutorX = Math.round(STAGE_INSET + size / 2 + TUTOR_ROUND_SHIFT.x * size)
    const face = (roundSize / 2) * TUTOR_FACE_RATIO
    const markFrom = face + MARK_GAP
    const markX = Math.min(tutorX + markFrom * Math.SQRT1_2, edge - TUTOR_MARK.width)
    const reach = Math.max(0, markX - tutorX)
    const markDown = Math.sqrt(Math.max(0, markFrom * markFrom - reach * reach))
    const right = Math.max(tutorX + roundSize / 2, markX + TUTOR_MARK.width)
    return { roundSize, tutorX, face, markX, markDown, right }
}

export function stageLayout(width: number, height: number, sharing = false, rounds = 0, twins = false): StageLayout {
    const me: Rect = {
        x: width - STAGE_INSET - THUMB.width,
        y: height - STAGE_INSET - THUMB.height,
        width: THUMB.width,
        height: THUMB.height,
    }
    const twinStrip: Rect | null = twins
        ? { x: me.x, y: me.y - TWIN_SEAM - TWIN_STRIP, width: me.width, height: TWIN_STRIP }
        : null
    // Le haut de ce que ma vignette occupe, sa bande comprise.
    const meTop = twinStrip?.y ?? me.y
    // Sam en petit, à côté du masteur : au-dessus de son prénom, ou de sa caméra quand il partage.
    const shared = sharing ? shareLayout(width, height, me, meTop) : null
    const samSmall = shared?.samSmall ?? {
        x: STAGE_INSET + SAM_SMALL / 2,
        y: height - STAGE_INSET - STAGE_NAME_HEIGHT - SAM_SMALL_GAP - SAM_SMALL / 2,
    }
    const roundRects = roundsLayout(rounds, width, me, meTop, shared?.share ?? null, samSmall)
    // Le bord droit de ce que le duo occupe : la colonne des petits ronds, quand il y en a, le garde.
    const edge = width - STAGE_INSET - (shared == null && roundRects.length > 0 ? ROUND + ROUND_GAP : 0)
    // Le duo, Sam devant et le masteur en retrait en haut à droite, légèrement superposés comme dans la
    // miniature : au coin bas gauche, le plus près de l'élève. S'il atteindrait ma vignette (et sa bande),
    // de son rond ou de son étiquette, il se pose au-dessus d'elle ; et il se réduit si ce que dit Sam n'y
    // tiendrait plus au-dessus de lui.
    const fullSize = Math.round(Math.min(width * 0.45, height * 0.28, 180))
    const samBottom =
        duoAcross(fullSize, edge).right + SAM_SMALL_GAP <= me.x ? height - STAGE_INSET : meTop - SAM_SMALL_GAP
    // Un pixel de plus pour l'arrondi du centre du rond.
    const size = Math.min(fullSize, Math.floor((samBottom - PEEK_GAP - SAM_PEEK_MAX_HEIGHT - 1) / DUO_REACH))
    const { roundSize, tutorX, face, markX, markDown } = duoAcross(size, edge)
    const samCenter = { x: STAGE_INSET + size / 2, y: samBottom - size / 2 }
    const tutorCenter = { x: tutorX, y: Math.round(samCenter.y + TUTOR_ROUND_SHIFT.y * size) }
    const tutorMark: Rect = { x: markX, y: tutorCenter.y + markDown, ...TUTOR_MARK }
    // Ce que dit Sam sort juste au-dessus de lui, sur son bord gauche : elle longe le rond du masteur, et
    // monte au besoin pour ne jamais couvrir son visage.
    const faceTop = tutorCenter.y - face
    const peek = {
        x: STAGE_INSET,
        y: Math.min(samCenter.y - size / 2, faceTop) - PEEK_GAP,
        width: edge - STAGE_INSET,
    }
    return {
        me,
        samFront: { size, center: samCenter },
        samSmall: { center: samSmall, scale: SAM_SMALL / size },
        tutorBack: { center: tutorCenter, scale: roundSize / Math.max(1, width, height) },
        tutorRound: { center: tutorCenter, size: roundSize },
        peek,
        tutorMark,
        share: shared?.share ?? null,
        rounds: roundRects,
        twins: twinStrip,
    }
}

/** Les pastilles d'état de la vidéo pleine du masteur, et son nom au plus large. */
export function tutorMarks(width: number, height: number): { micOff: Rect; hand: Rect; name: Rect } {
    return {
        micOff: { x: width - MARK_INSET - STAGE_MARK, y: MARK_INSET, width: STAGE_MARK, height: STAGE_MARK },
        hand: { x: MARK_INSET, y: MARK_INSET, width: STAGE_MARK, height: STAGE_MARK },
        name: {
            x: STAGE_INSET,
            y: height - STAGE_INSET - STAGE_NAME_HEIGHT,
            width: width * STAGE_NAME_MAX_RATIO,
            height: STAGE_NAME_HEIGHT,
        },
    }
}

/** Une place posée depuis le bas de la scène, et non depuis le haut. */
export type BottomPlace = { left: number; bottom: number; width: number; height: number }

/**
 * Une place de la règle, comptée depuis le bas d'une scène haute de `height`. Ce qui se tient en bas (le
 * duo, ma vignette et sa bande) y est posé : quand la scène perd de la hauteur par le haut (une barre qui
 * s'ouvre au-dessus d'elle), sa distance au bas ne change pas, et il ne bouge pas, même pendant l'image
 * où la scène n'est pas encore remesurée.
 */
export function fromBottom(rect: Rect, height: number): BottomPlace {
    return { left: rect.x, bottom: height - rect.y - rect.height, width: rect.width, height: rect.height }
}

/** Un carré de centre et de côté donnés. */
export function square(center: Point, size: number): Rect {
    return { x: center.x - size / 2, y: center.y - size / 2, width: size, height: size }
}

export function overlaps(a: Rect, b: Rect): boolean {
    return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height
}
